/** Deputy's set-outs (writs of ejectment): list on the Schedule screen, and recording the result at the scene. */
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { OUTCOME_LABEL, ResultForm, fmtWhen, setAgencyTz } from '../shared/setout.tsx';
import { Card, ErrorBox, Notice, Spinner, useLoad } from '../shared/ui.tsx';

export function MySetOutsCard({ online }: { online: boolean }) {
  const { me } = useAuth();
  setAgencyTz(me?.agency.timezone);
  const r = useLoad(() => (online ? api.get<any>('/api/setouts/mine') : Promise.resolve(null)), [online]);
  if (!online || !r.data || !r.data.setOuts.length) return null;
  return (
    <Card title="My set-outs">
      <ul className="plain so-mine">
        {r.data.setOuts.map((s: any) => (
          <li key={s.id} className={s.needsResult ? 'alert' : ''}>
            <div className="grow">
              <strong>{fmtWhen(s.scheduledFor)}</strong> · {s.defendant}
              <div className="small">{s.address}</div>
              <div className="small muted">
                With {s.deputies.filter((d: any) => d.id !== me?.user.id).map((d: any) => d.name).join(', ') || '—'}
                {s.status === 'completed' ? ` · ${OUTCOME_LABEL[s.outcome]}` : s.landlord.confirmedAt ? ' · landlord confirmed' : ' · landlord not confirmed'}
              </div>
            </div>
            {s.status === 'scheduled' && (
              <Link to={`/field/setout/${s.id}`} className="btn sm primary">
                {s.needsResult ? 'Record now' : 'Open'}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function SetOutScreen({ id, online }: { id: string; online: boolean }) {
  const { me } = useAuth();
  setAgencyTz(me?.agency.timezone);
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/setouts/${id}`), [id]);
  const meta = useLoad(() => api.get<any>('/api/meta'));
  if (!online) return <Notice tone="warn">Recording a set-out needs a connection.</Notice>;
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { setOut: s, paper: p } = r.data;
  return (
    <div className="stack so-screen">
      <Link to="/field/schedule">← Schedule</Link>
      <div>
        <h1>Set-out · {p.defendant}</h1>
        <div>{p.address}</div>
        <div className="small muted">
          {fmtWhen(s.scheduledFor)} · case <span className="mono">{p.caseNumber}</span> · {p.number}
        </div>
      </div>
      <div className="row gap-sm wrap">
        <a className="btn" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address)}`} target="_blank" rel="noreferrer">
          Navigate
        </a>
        {s.landlord.phone && (
          <a className="btn" href={`tel:${s.landlord.phone.replace(/[^\d+]/g, '')}`}>
            📞 Call landlord
          </a>
        )}
      </div>
      {s.landlord.cancelRequestedAt && <Notice tone="bad">Landlord asked to cancel: “{s.landlord.cancelReason}”. Check with your supervisor before going.</Notice>}
      {s.notes && <Notice tone="info">{s.notes}</Notice>}
      {s.status !== 'scheduled' ? (
        <Notice tone="ok">Already recorded: {OUTCOME_LABEL[s.outcome] ?? s.status}.</Notice>
      ) : (
        <Card title="What happened">
          <ResultForm big setOut={s} deputies={(meta.data?.servers ?? []).map((x: any) => ({ id: x.id, name: x.display_name }))} onDone={() => navigate('/field/schedule')} />
        </Card>
      )}
    </div>
  );
}
